import React from "react";

import { cssVariables } from "./css_variables";
import { Pressable } from "./pressable";
import { PulseLoader } from "./pulse_loader";
import { stylex } from "./stylex";
import { Text, TextColor } from "./text";

type ButtonAppearance = "flat" | "full" | "outline";
type ButtonColor = "primary" | "danger";

type Style =
  | "fullDisabled"
  | "outlineDisabled"
  | "fullPrimary"
  | "outlinePrimary"
  | "fullDanger"
  | "outlineDanger";

interface ButtonProps {
  title?: string;
  onPress?: () => void;
  appearance?: ButtonAppearance;
  color?: ButtonColor;
  disabled?: boolean;
  loading?: boolean;
  paddingHorizontal?: number;
  testID?: string;
}

/**
 * A button commonly used for call-to-actions.
 */
export function Button(props: ButtonProps) {
  const {
    title,
    onPress = () => {},
    testID,
    color = "primary",
    appearance = "full",
    paddingHorizontal = 24,
    loading = false,
    disabled = false,
  } = props;

  const effectiveDisabled = loading || disabled;

  let style: Style = {
    full: {
      primary: "fullPrimary" as const,
      danger: "fullDanger" as const,
    },
    outline: {
      primary: "outlinePrimary" as const,
      danger: "outlineDanger" as const,
    },
    flat: {
      primary: "outlinePrimary" as const,
      danger: "outlineDanger" as const,
    },
  }[appearance][color];

  if (effectiveDisabled) {
    style = {
      full: {
        primary: "fullDisabled" as const,
        danger: "fullDisabled" as const,
      },
      outline: {
        primary: "outlineDisabled" as const,
        danger: "outlineDisabled" as const,
      },
      flat: {
        primary: "fullDisabled" as const,
        danger: "fullDisabled" as const,
      },
    }[appearance][color];
  }

  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      disabled={effectiveDisabled}
      style={({ hovered }) => ({
        ...styles("root", style, hovered && "hover"),
        ...(paddingHorizontal && {
          paddingLeft: paddingHorizontal,
          paddingRight: paddingHorizontal,
        }),
      })}
    >
      {loading ? (
        <PulseLoader size={12} color="white" />
      ) : (
        <Text
          isBold={appearance === "full"}
          color={getTextColor(appearance, color, effectiveDisabled)}
          uppercase
        >
          {title}
        </Text>
      )}
    </Pressable>
  );
}

function getTextColor(
  appearance: ButtonAppearance,
  color: ButtonColor,
  disabled: boolean
): TextColor {
  if (disabled) {
    if (appearance === "full") {
      return "white";
    }

    return "primary";
  }

  return {
    full: {
      primary: "white" as const,
      danger: "white" as const,
    },
    outline: {
      primary: "primary" as const,
      danger: "error" as const,
    },
    flat: {
      primary: "primary" as const,
      danger: "error" as const,
    },
  }[appearance][color];
}

const styles = stylex.create({
  root: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "none",
    boxSizing: "border-box",
    backgroundColor: "none",
    paddingLeft: 24,
    paddingRight: 24,
    minWidth: 144,
    paddingTop: 8,
    paddingBottom: 8,
    borderRadius: cssVariables.radius,
    height: 40,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "transparent",
    outline: "none",
    cursor: "pointer",
    textDecoration: "none",
    transition: "0.2s",
  },
  fullDisabled: {
    cursor: "auto",
    backgroundColor: cssVariables.buttonPrimaryDisabled,
    borderColor: "transparent",
  },
  outlineDisabled: {
    cursor: "auto",
    borderColor: cssVariables.borderColorPrimary,
    opacity: 0.2,
  },
  fullPrimary: {
    backgroundColor: cssVariables.backgroundPrimary,
  },
  outlinePrimary: {
    borderColor: cssVariables.borderColorPrimary,
    backgroundColor: "white",
  },
  fullDanger: {
    backgroundColor: cssVariables.textColorError,
  },
  outlineDanger: {
    borderColor: cssVariables.borderColorDanger,
    backgroundColor: "white",
  },
  hover: {
    boxShadow: "inset 0 0 490px rgba(0, 0, 0, 0.03)",
  },
});
